// src/modules/application/pages/AdminApplicationsByCategoryPage.tsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  useGetAdminApplicationsByCategoryQuery,
} from "../api/probationerApplicationApi";

type AdminApplication = {
  id: string;
  user_id?: number;
  logbook_diet_id?: string;
  application_category_id?: number;

  surname?: string;
  other_names?: string;
  title?: string;

  status?: string;
  completion_step?: number;
  created_at?: string;

  // ✅ OPTIONAL (only if your API returns these)
  reviewer_id?: string | number | null;
  reviewer?: { id?: string | number; name?: string; email?: string } | null;

  user?: { id?: number; name?: string; email?: string };
  category?: { id?: number; name?: string };

  diet?: {
    id: string;
    title?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    is_active?: number | boolean;
    created_at?: string;
  };
};

type NavState = {
  categoryName?: string;
  categoryId?: number;
};

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong"
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
}

/** ✅ normalize any status */
type KnownStatus =
  | "approved"
  | "acknowledged"
  | "under_review"
  | "with_committee"
  | "rejected"
  | "returned"
  | "submitted"
  | "pending";

type TabKey =
  | "all"
  | "queue" // everything still in review — see QUEUE_STATUSES
  | KnownStatus
  | "other";

/**
 * What the default "Queue" tab means: an application somebody at the institute
 * is still holding. It used to be pending + submitted, and `submitted` is the
 * retired name for what is now `under_review` — leaving it at that would have
 * the landing tab show nothing but unsubmitted drafts.
 */
const QUEUE_STATUSES: KnownStatus[] = [
  "acknowledged",
  "under_review",
  "with_committee",
  "submitted",
];

function normalizeKey(status?: any): KnownStatus | "other" {
  const s = String(status || "")
    .toLowerCase()
    .trim();
  // `awaiting_endorsements` is an older name for the `acknowledged` rung — the
  // two are one bucket rather than one of them falling into "other".
  if (s === "awaiting_endorsements") return "acknowledged";
  if (
    s === "approved" ||
    s === "acknowledged" ||
    s === "under_review" ||
    s === "with_committee" ||
    s === "rejected" ||
    s === "returned" ||
    s === "submitted" ||
    s === "pending"
  )
    return s;
  return "other";
}

function labelForTab(tab: TabKey) {
  if (tab === "queue") return "Queue";
  if (tab === "approved") return "Accepted";
  if (tab === "acknowledged") return "Awaiting endorsements";
  if (tab === "under_review") return "Secretariat review";
  if (tab === "with_committee") return "With committee";
  if (tab === "rejected") return "Rejected";
  if (tab === "returned") return "Returned";
  if (tab === "submitted") return "Submitted";
  if (tab === "pending") return "Pending";
  if (tab === "other") return "Other";
  return "All";
}

/** ✅ shared color system (tabs + pills + badge) */
function statusColors(key: TabKey | KnownStatus | "other") {
  if (key === "approved")
    return {
      border: "border-green-200",
      active: "bg-green-600 text-white border-green-600",
      inactive: "bg-green-50 text-green-800 hover:bg-green-100",
      pill: "bg-green-100 text-green-800 border-green-200",
    };

  if (key === "acknowledged")
    return {
      border: "border-violet-200",
      active: "bg-violet-600 text-white border-violet-600",
      inactive: "bg-violet-50 text-violet-800 hover:bg-violet-100",
      pill: "bg-violet-100 text-violet-800 border-violet-200",
    };

  if (key === "under_review")
    return {
      border: "border-blue-200",
      active: "bg-blue-600 text-white border-blue-600",
      inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100",
      pill: "bg-blue-100 text-blue-800 border-blue-200",
    };

  if (key === "with_committee")
    return {
      border: "border-purple-200",
      active: "bg-purple-600 text-white border-purple-600",
      inactive: "bg-purple-50 text-purple-800 hover:bg-purple-100",
      pill: "bg-purple-100 text-purple-800 border-purple-200",
    };

  if (key === "rejected")
    return {
      border: "border-red-200",
      active: "bg-red-600 text-white border-red-600",
      inactive: "bg-red-50 text-red-800 hover:bg-red-100",
      pill: "bg-red-100 text-red-800 border-red-200",
    };

  if (key === "returned")
    return {
      border: "border-purple-200",
      active: "bg-purple-600 text-white border-purple-600",
      inactive: "bg-purple-50 text-purple-800 hover:bg-purple-100",
      pill: "bg-purple-100 text-purple-800 border-purple-200",
    };

  if (key === "submitted")
    return {
      border: "border-sky-200",
      active: "bg-sky-600 text-white border-sky-600",
      inactive: "bg-sky-50 text-sky-800 hover:bg-sky-100",
      pill: "bg-sky-100 text-sky-800 border-sky-200",
    };

  if (key === "pending")
    return {
      border: "border-yellow-200",
      active: "bg-yellow-600 text-white border-yellow-600",
      inactive: "bg-yellow-50 text-yellow-900 hover:bg-yellow-100",
      pill: "bg-yellow-100 text-yellow-900 border-yellow-200",
    };

  if (key === "queue")
    return {
      border: "border-orange-200",
      active: "bg-orange-600 text-white border-orange-600",
      inactive: "bg-orange-50 text-orange-900 hover:bg-orange-100",
      pill: "bg-orange-100 text-orange-900 border-orange-200",
    };

  if (key === "other")
    return {
      border: "border-gray-200",
      active: "bg-gray-800 text-white border-gray-800",
      inactive: "bg-gray-50 text-gray-800 hover:bg-gray-100",
      pill: "bg-gray-100 text-gray-800 border-gray-200",
    };

  // all
  return {
    border: "border-black",
    active: "bg-black text-white border-black",
    inactive: "bg-white text-gray-800 hover:bg-gray-50",
    pill: "bg-gray-100 text-gray-800 border-gray-200",
  };
}

function tabStyles(tab: TabKey, active: boolean) {
  const c = statusColors(tab);
  return active
    ? ["border", "rounded-md", c.active].join(" ")
    : ["border", "rounded-md", c.border, c.inactive].join(" ");
}

function CountPill({ tab, count }: { tab: TabKey; count: number }) {
  const c = statusColors(tab);
  return (
    <span
      className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}
    >
      {count}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const k = normalizeKey(status);
  const c = statusColors(k);
  const s = String(status || "pending").toLowerCase();

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${c.pill}`}
      title={s}
    >
      {s}
    </span>
  );
}

/** ✅ Diet helpers */
function dietLabel(a: AdminApplication) {
  const d = a.diet;
  if (d?.title) return d.title;
  if (d?.id) return `Diet ${String(d.id).slice(0, 8)}…`;
  if (a.logbook_diet_id)
    return `Diet ${String(a.logbook_diet_id).slice(0, 8)}…`;
  return "Unknown Diet";
}

function dietMeta(a: AdminApplication) {
  const d = a.diet;
  const start = d?.start_date ? fmtDate(d.start_date) : "—";
  const end = d?.end_date ? fmtDate(d.end_date) : "—";
  const st = d?.status ? String(d.status) : "";
  return `${start} – ${end}${st ? ` • ${st}` : ""}`;
}

/** ✅ UI rows */
function CardRow({ a }: { a: AdminApplication }) {
  const fullName = [a.title, a.surname, a.other_names]
    .filter(Boolean)
    .join(" ");
  const userLine = [a.user?.name, a.user?.email].filter(Boolean).join(" • ");
  const st = String(a.status || "pending").toLowerCase();

  const reviewerLine =
    a.reviewer?.name || a.reviewer?.email
      ? `Reviewer: ${[a.reviewer?.name, a.reviewer?.email]
          .filter(Boolean)
          .join(" • ")}`
      : a.reviewer_id
      ? `Reviewer: ${String(a.reviewer_id)}`
      : "Reviewer: —";

  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold">{fullName || a.id}</div>
            <StatusBadge status={st} />
          </div>

          {userLine && <div className="text-sm text-gray-600">{userLine}</div>}

          <div className="text-sm text-gray-600">{reviewerLine}</div>

          <div className="text-sm text-gray-600">
            Step: <span className="font-medium">{a.completion_step ?? 0}</span>{" "}
            • Created:{" "}
            <span className="font-medium">{fmtDate(a.created_at)}</span>
          </div>

          <div className="text-xs text-gray-500 break-all">ID: {a.id}</div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/admin-probationer-applications/${a.id}`}
            className="px-3 py-2 rounded-md border text-sm"
          >
            View
          </Link>
        </div>
      </div>
    </div>
  );
}

function DietSection({
  title,
  meta,
  count,
  rightAction,
  children,
}: {
  title: string;
  meta: string;
  count: number;
  rightAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border rounded-xl bg-white">
      <div className="p-4 border-b">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">{title}</div>
            <div className="text-xs text-gray-600 mt-1">{meta}</div>
          </div>

          <div className="flex items-center gap-2">
            {rightAction}
            <span className="inline-flex items-center px-2 py-1 text-xs border rounded-full bg-gray-50 text-gray-800 border-gray-200">
              {count} application{count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

/** ✅ dynamic summary card */
function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: TabKey;
}) {
  const bg =
    tone === "all"
      ? "bg-gray-50 border-gray-200"
      : tone === "queue"
      ? "bg-orange-50 border-orange-200"
      : tone === "approved"
      ? "bg-green-50 border-green-200"
      : tone === "acknowledged"
      ? "bg-blue-50 border-blue-200"
      : tone === "rejected"
      ? "bg-red-50 border-red-200"
      : tone === "returned"
      ? "bg-purple-50 border-purple-200"
      : tone === "submitted"
      ? "bg-sky-50 border-sky-200"
      : tone === "pending"
      ? "bg-yellow-50 border-yellow-200"
      : "bg-gray-50 border-gray-200";

  const numCls =
    tone === "approved"
      ? "text-green-700"
      : tone === "acknowledged"
      ? "text-blue-700"
      : tone === "rejected"
      ? "text-red-700"
      : tone === "returned"
      ? "text-purple-700"
      : tone === "submitted"
      ? "text-sky-700"
      : tone === "pending"
      ? "text-yellow-800"
      : tone === "queue"
      ? "text-orange-800"
      : "text-gray-800";

  return (
    <div className={`border rounded-xl p-3 ${bg}`}>
      <div className="text-xs text-gray-600">{label}</div>
      <div className={`text-xl font-bold ${numCls}`}>{value}</div>
    </div>
  );
}

export default function AdminApplicationsByCategoryPage() {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const cid = Number(categoryId);

  const location = useLocation();
  const navState = (location.state || {}) as NavState;

  const { data, isLoading, isFetching, error, refetch } =
    useGetAdminApplicationsByCategoryQuery({ categoryId: cid }, { skip: !cid });

  const busy = isLoading || isFetching;

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  // ✅ backend mutation (auto-assign)

  // ✅ to show which diet is currently being assigned

  const list = useMemo(() => {
    const d: any = data;
    if (!d) return [] as AdminApplication[];
    if (Array.isArray(d)) return d as AdminApplication[];
    if (Array.isArray(d.data)) return d.data as AdminApplication[];
    return [] as AdminApplication[];
  }, [data]);

  const categoryName = useMemo(() => {
    const fromState = navState?.categoryName?.trim();
    if (fromState) return fromState;

    const fromApi = list[0]?.category?.name?.trim();
    if (fromApi) return fromApi;

    return cid ? `Category #${cid}` : "Category";
  }, [navState?.categoryName, list, cid]);

  /** ✅ build ALL tabs based on data */
  const tabData = useMemo(() => {
    const counts = new Map<TabKey, number>();

    const keys: TabKey[] = [
      "pending",
      "submitted",
      "acknowledged",
      "under_review",
      "with_committee",
      "approved",
      "returned",
      "rejected",
      "other",
      "all",
      "queue",
    ];
    for (const k of keys) counts.set(k, 0);

    for (const a of list) {
      const k = normalizeKey(a.status);
      counts.set(k, (counts.get(k) || 0) + 1);
    }

    const queueCount = QUEUE_STATUSES.reduce(
      (total, k) => total + (counts.get(k) || 0),
      0,
    );
    counts.set("queue", queueCount);

    counts.set("all", list.length);

    const ordered: TabKey[] = [
      "queue",
      "pending",
      "submitted",
      "acknowledged",
      "under_review",
      "with_committee",
      "approved",
      "returned",
      "rejected",
      "other",
      "all",
    ];

    const tabs = ordered.filter((k) => {
      if (k === "all" || k === "queue") return true;
      return (counts.get(k) || 0) > 0;
    });

    return { tabs, counts };
  }, [list]);

  const [tab, setTab] = useState<TabKey>("queue");

  const visible = useMemo(() => {
    if (tab === "all") return list;

    if (tab === "queue") {
      return list.filter((a) =>
        QUEUE_STATUSES.includes(normalizeKey(a.status) as KnownStatus),
      );
    }

    return list.filter((a) => normalizeKey(a.status) === tab);
  }, [list, tab]);

  // ✅ Group visible items by diet
  const groupedByDiet = useMemo(() => {
    const map = new Map<
      string,
      {
        dietId: string;
        title: string;
        meta: string;
        items: AdminApplication[];
        sortKey: string;
      }
    >();

    for (const a of visible) {
      const id = a.diet?.id || a.logbook_diet_id || "unknown";
      const title = dietLabel(a);
      const meta = dietMeta(a);
      const sortKey =
        a.diet?.start_date || a.diet?.created_at || a.created_at || "";

      if (!map.has(id)) {
        map.set(id, { dietId: id, title, meta, items: [], sortKey });
      }
      map.get(id)!.items.push(a);
    }

    return Array.from(map.values()).sort((x, y) =>
      String(y.sortKey || "").localeCompare(String(x.sortKey || ""))
    );
  }, [visible]);

  const clearAlerts = () => {
    setLocalError(null);
    setLocalSuccess(null);
  };

  // ✅ Helper: count submitted + unassigned in a diet group (UI only)
  function countSubmittedUnassigned(items: AdminApplication[]) {
    return items.filter((a) => {
      const st = normalizeKey(a.status);
      const unassigned =
        a.reviewer_id === null ||
        a.reviewer_id === undefined ||
        a.reviewer_id === "" ||
        (a as any).reviewerId === null ||
        (a as any).reviewerId === undefined;
      return st === "submitted" && unassigned;
    }).length;
  }


  return (
    <div className="w-full mx-auto p-4 md:p-8">
      {/* ✅ header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-sm text-gray-600">
            <Link to="/admin/dashboard" className="underline">
              Dashboard
            </Link>{" "}
            /{" "}
            <Link to="/admin/applications/categories" className="underline">
              Categories
            </Link>{" "}
            / Category Applications
          </div>

          <h1 className="leading-tight text-2xl font-bold">
            {categoryName} Applications
          </h1>

          <p className="text-sm text-gray-600">
            Category: <span className="font-medium">{categoryName}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
            disabled={busy}
          >
            Back
          </button>

          <button
            onClick={async () => {
              clearAlerts();
              try {
                await refetch();
                setLocalSuccess("Refreshed.");
                window.setTimeout(() => setLocalSuccess(null), 600);
              } catch (e: any) {
                setLocalError(safeMsg(e));
              }
            }}
            className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
            disabled={busy}
          >
            {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {localSuccess && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
          {localSuccess}
        </div>
      )}

      {localError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {localError}
        </div>
      )}

      {error && !localError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {safeMsg(error)}
        </div>
      )}

      {/* ✅ Summary cards */}
      {!busy && !error && (
        <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-10 gap-3 mb-4">
          {tabData.tabs
            .filter((k) => k !== "all")
            .map((k) => (
              <SummaryCard
                key={k}
                label={labelForTab(k)}
                value={tabData.counts.get(k) || 0}
                tone={k}
              />
            ))}
          <SummaryCard
            label="All"
            value={tabData.counts.get("all") || 0}
            tone="all"
          />
        </div>
      )}

      {/* ✅ Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tabData.tabs.map((k) => {
          const count = tabData.counts.get(k) || 0;
          const active = tab === k;

          return (
            <button
              key={k}
              onClick={() => {
                clearAlerts();
                setTab(k);
              }}
              className={[
                "px-3 py-2 text-sm transition",
                tabStyles(k, active),
              ].join(" ")}
              disabled={busy}
            >
              {labelForTab(k)}
              <CountPill tab={k} count={count} />
            </button>
          );
        })}
      </div>

      {busy && <div className="text-sm">Loading…</div>}

      {!busy && visible.length === 0 && (
        <div className="p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
          No applications found for <b>{labelForTab(tab)}</b>.
        </div>
      )}

      {/* ✅ Grouped by diet */}
      {!busy && visible.length > 0 && (
        <div className="space-y-4">
          {groupedByDiet.map((g) => {
            const submittedUnassigned = countSubmittedUnassigned(g.items);


            return (
              <DietSection
                key={g.dietId}
                title={g.title}
                meta={g.meta}
                count={g.items.length}
                rightAction={
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline-flex items-center px-2 py-1 text-xs border rounded-full bg-sky-50 text-sky-800 border-sky-200">
                      {submittedUnassigned} unassigned submitted
                    </span>

                    {/* Applications are placed with a reviewer deliberately,
                        from the diet's review-assignment screen. */}
                    {g.dietId !== "unknown" ? (
                      <Link
                        to={`/probationer-applications/admin/submissions/diets/${g.dietId}/assignments`}
                        className="px-3 py-2 rounded-md text-sm border bg-gray-900 text-white hover:bg-gray-800"
                        title="Assign these applications to the Membership Committee"
                      >
                        Review Assignment
                      </Link>
                    ) : null}
                  </div>
                }
              >
                {g.items.map((a) => (
                  <CardRow key={a.id} a={a} />
                ))}
              </DietSection>
            );
          })}
        </div>
      )}
    </div>
  );
}
